import { useCallback, useRef, useState } from 'react'
import type { RichTextEditorHandle } from '@/components/ui/RichTextEditor'
import { ARTICLE_IMAGE_LIMIT } from '@/domain/article.types'
import { publicAssetUrl } from '@/lib/article-html'
import { notification } from '@/notifications/notification-store'
import { enqueue } from '@/upload/upload-store'

/**
 * Dropping an image into the body: it appears where it was dropped, and its upload
 * runs in the background.
 *
 * The placement is made immediately from a local `blob:` preview, so the author
 * never waits on the network to keep writing, and the preview is swapped for the
 * file's stored URL the moment the server confirms it. An upload that fails takes
 * its own placement back out — a preview left behind would look saved while pointing
 * at bytes that exist in this tab only.
 *
 * `imageFileIds` is still the article's own list and still what the API stores; a
 * confirmed upload appends to it through `onImported`. The body holds PLACEMENTS of
 * those images, and the two are not reconciled: deleting an `<img>` from the prose
 * does not un-own the file.
 */

/** Tags these uploads in the shared queue, so another surface's list never shows them. */
export const ARTICLE_IMAGE_UPLOAD_OWNER = 'article-images'

export interface ArticleImageDrop {
	/** Place dropped image files at a document position and upload them in the background. */
	onDropFiles: (files: File[], position: number) => void
	/**
	 * An upload is still running, so the body holds at least one `blob:` preview.
	 * Saving now would store a URL that dies with the tab, which is why the composer
	 * holds its submit until this clears.
	 */
	isUploading: boolean
}

/**
 * Place one file and upload it, reporting through `onSettled` when the upload has
 * finished either way.
 *
 * Module-level so the preview's whole lifecycle — created, placed, swapped or
 * removed, revoked — reads as one unit.
 */
function placeAndUpload(
	file: File,
	position: number,
	editorRef: React.RefObject<RichTextEditorHandle | null>,
	onImported: (fileId: string) => void,
	onSettled: () => void,
): void {
	// A local preview, so the image is on the page before a single byte has been
	// sent. Revoked once its outcome is known.
	const preview = URL.createObjectURL(file)
	editorRef.current?.insertImage({ src: preview, alt: file.name }, position)

	const settle = () => {
		URL.revokeObjectURL(preview)
		onSettled()
	}

	enqueue(file, {
		owner: ARTICLE_IMAGE_UPLOAD_OWNER,
		// Public, so the `src` written into the stored body never expires.
		isPublic: true,
		onConfirmed: (confirmed) => {
			const stored = confirmed.s3Key ? publicAssetUrl(confirmed.s3Key) : null
			// No public asset origin configured: the sanitizer drops the image on
			// render, so the placement goes with it rather than surviving as a preview
			// that resolves in this tab and nowhere else.
			if (stored) editorRef.current?.replaceImageSrc(preview, stored)
			else editorRef.current?.removeImage(preview)
			onImported(confirmed.fileId)
			settle()
		},
		onFailed: (message) => {
			editorRef.current?.removeImage(preview)
			notification.error(`${file.name} could not be added to the article. ${message}`)
			settle()
		},
	})
}

/**
 * Why a drop of `count` files is refused, or `null` when there is room for all of
 * them. Answered before anything is sent, so an over-cap drop leaves no orphaned
 * objects in the public bucket behind a save the API would refuse.
 */
function refusal(count: number, remaining: number): string | null {
	if (count <= remaining) return null
	if (remaining <= 0) return `This article already holds the maximum of ${ARTICLE_IMAGE_LIMIT} images.`
	return `Only ${remaining} more image${remaining === 1 ? '' : 's'} can be added to this article.`
}

export function useArticleImageDrop(
	imageFileIds: readonly string[],
	onImported: (fileId: string) => void,
	editorRef: React.RefObject<RichTextEditorHandle | null>,
): ArticleImageDrop {
	// Counted rather than read off the upload queue: subscribing to the store would
	// re-render the composer on every progress tick, and all it needs to know is
	// WHETHER anything is in flight.
	const [uploading, setUploading] = useState(0)
	// The same count, readable without waiting for a render, so each file in one
	// gesture is checked against the room the files before it already took.
	const inFlight = useRef(0)

	const place = useCallback(
		(file: File, position: number) => {
			inFlight.current += 1
			setUploading((count) => count + 1)
			placeAndUpload(file, position, editorRef, onImported, () => {
				inFlight.current -= 1
				setUploading((count) => count - 1)
			})
		},
		[editorRef, onImported],
	)

	const onDropFiles = useCallback(
		(files: File[], position: number) => {
			// In-flight uploads count against the cap: their ids are not on the article
			// yet, and the API would refuse the save once they were.
			const refused = refusal(files.length, ARTICLE_IMAGE_LIMIT - imageFileIds.length - inFlight.current)
			if (refused) {
				notification.error(refused)
				return
			}
			// Each image occupies one position, so `position + index` keeps a multi-file
			// drop in the order it was dropped.
			files.forEach((file, index) => place(file, position + index))
		},
		[imageFileIds.length, place],
	)

	return { onDropFiles, isUploading: uploading > 0 }
}
